import { useState, useEffect } from 'react';
import { 
  Activity, Droplet, Utensils, Wind, Scissors, Dumbbell, TrendingUp, 
  AlertCircle, User, Clock, History, Edit, Bell, CheckCircle2, 
  AlertTriangle, XCircle, ChevronDown, ChevronUp, TrendingDown, 
  Minus, Eye, MessageSquare, Maximize2, Minimize2, ChevronsDown, ChevronsUp
} from 'lucide-react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { nursesApi } from '../../lib/api/nurses';
import { LoadingState } from '../common/LoadingState';
import { format, parseISO } from 'date-fns';

interface TaskData {
  [key: string]: {
    dropdown?: string;
    result?: string;
    timeTaken?: string;
    comments?: string;
  };
}

interface TaskRecord {
  id: number;
  task_category: string;
  task_data: TaskData;
  recorded_at: string;
  notes?: string;
  nurse_name?: string;
  created_at?: string;
}

interface HomeNurseTasksViewCompactProps {
  patientId: string;
  patientName: string;
  patientAge?: number;
  patientGender?: string;
  diagnosis?: string;
}

export function HomeNurseTasksViewCompact({ 
  patientId, 
  patientName,
  patientAge,
  patientGender,
  diagnosis
}: HomeNurseTasksViewCompactProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [vitalsData, setVitalsData] = useState<TaskRecord | null>(null);
  const [chestDrainData, setChestDrainData] = useState<TaskRecord | null>(null);
  const [nutritionData, setNutritionData] = useState<TaskRecord | null>(null);
  const [tracheostomyData, setTracheostomyData] = useState<TaskRecord | null>(null);
  const [woundDressingData, setWoundDressingData] = useState<TaskRecord | null>(null);
  const [physiotherapyData, setPhysiotherapyData] = useState<TaskRecord | null>(null);
  const [intakeOutputData, setIntakeOutputData] = useState<TaskRecord | null>(null);
  const [selectedDateFilter, setSelectedDateFilter] = useState<string | undefined>(undefined);

  
  // ✅ ALL SECTIONS EXPANDED BY DEFAULT - Doctor doesn't need to click arrows
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['vitals', 'chest_drain', 'nutrition', 'tracheostomy', 'wound_dressing', 'physiotherapy', 'intake_output'])
  );
  const [compactMode, setCompactMode] = useState(true);

  useEffect(() => {
    fetchAllTaskData();
  }, [patientId]);

  const fetchAllTaskData = async (
    startDate: string | undefined = undefined,
    endDate: string | undefined = undefined
  ) => {
    setIsLoading(true);

    try {
      const today = new Date().toISOString().split("T")[0];

      // If startDate is undefined → use today's date automatically
      startDate = startDate ?? today;
      endDate = endDate ?? today; 
      
      const categories = ['vitals', 'chest_drain', 'nutrition', 'tracheostomy', 'wound_dressing', 'physiotherapy', 'intake_output'];
      
      const results = await Promise.all(
        categories.map(category => 
          nursesApi.getNurseTaskData(parseInt(patientId), category, startDate, endDate, 1)
            .then(response => ({ category, data: response?.records || null }))
            .catch(() => ({ category, data: null }))
        )
      );

      results.forEach(({ category, data }) => {
        switch (category) {
          case 'vitals':
            setVitalsData(data);
            break;
          case 'chest_drain':
            setChestDrainData(data);
            break;
          case 'nutrition':
            setNutritionData(data);
            break;
          case 'tracheostomy':
            setTracheostomyData(data);
            break;
          case 'wound_dressing':
            setWoundDressingData(data);
            break;
          case 'physiotherapy':
            setPhysiotherapyData(data);
            break;
          case 'intake_output':
            setIntakeOutputData(data);
            break;
        }
      });
    } catch (error) {
      console.error('Error fetching nurse task data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSection = (sectionKey: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionKey)) {
        newSet.delete(sectionKey);
      } else {
        newSet.add(sectionKey);
      }
      return newSet;
    });
  };

  // ✅ EXPAND ALL / COLLAPSE ALL functionality
  const expandAllSections = () => {
    setExpandedSections(new Set(['vitals', 'chest_drain', 'nutrition', 'tracheostomy', 'wound_dressing', 'physiotherapy', 'intake_output']));
  };

  const collapseAllSections = () => {
    setExpandedSections(new Set());
  };

  const allSectionsExpanded = expandedSections.size === 7;

  const formatTime = (dateString: string): string => {
    try {
      const date = parseISO(dateString);
      return format(date, 'dd MMM yyyy hh:mm a');
    } catch (error) {
      return '--:--';
    }
  };

  // ✅ HEALTHKON BRAND COLORS
  // Primary: #2762AC (blue)
  // Secondary: #F07D13 (orange)
  // Accent: #14CAF4 (aqua)
  // Neutral: #494949 (grey)

  // Check if value is abnormal
  const getAbnormalityStatus = (value: string): 'critical' | 'warning' | 'normal' | 'none' => {
    const valueLower = value.toLowerCase();
    
    // Critical/Abnormal - RED ALERT
    if (['bloody', 'infection', 'rejected', 'poor', 'septic', 'high', 'low', 'abnormal', 'critical'].some(term => valueLower.includes(term))) {
      return 'critical';
    }
    // Warning - ORANGE
    if (['purulent', 'moderate', 'minimal', 'reduced', 'serous', 'amber', 'concern'].some(term => valueLower.includes(term))) {
      return 'warning';
    }
    // None/Absent - GREY
    if (['none', 'absent', 'nil', 'no'].some(term => valueLower === term)) {
      return 'none';
    }
    return 'normal';
  };

  // ✅ Compact badge with HEALTHKON COLORS & RED ALERTS
  const getCompactBadge = (value: string) => {
    const status = getAbnormalityStatus(value);
    
    const baseClasses = "text-xs px-2 py-0.5 rounded flex items-center gap-1";
    
    switch (status) {
      case 'critical':
        // ✅ RED ALERT - Grabs doctor's attention!
        return (
          <Badge className={`${baseClasses} bg-red-100 text-red-700 border border-red-500 font-semibold`}>
            <AlertTriangle className="w-3 h-3 text-red-700" />
            {value}
          </Badge>
        );
      case 'warning':
        // ✅ ORANGE WARNING - Healthkon secondary color
        return (
          <Badge className={`${baseClasses} bg-[#FFF4E6] text-[#F07D13] border border-[#F07D13]`}>
            <AlertCircle className="w-3 h-3 text-[#F07D13]" />
            {value}
          </Badge>
        );
      case 'none':
        // ✅ GREY - Healthkon neutral color
        return <Badge className={`${baseClasses} bg-gray-100 text-[#494949] border border-gray-300`}>{value}</Badge>;
      default:
        // ✅ BLUE - Healthkon primary color for normal values
        return <Badge className={`${baseClasses} bg-[#E8F2FC] text-[#2762AC] border border-[#2762AC]/30`}>{value}</Badge>;
    }
  };

  // Get compact icon
  const getCompactIcon = (fieldName: string) => {
    const fieldLower = fieldName.toLowerCase();
    if (fieldLower.includes('temp')) return '🌡️';
    if (fieldLower.includes('heart') || fieldLower.includes('hr')) return '❤️';
    if (fieldLower.includes('spo2') || fieldLower.includes('oxygen')) return '🫁';
    if (fieldLower.includes('bp')) return '💉';
    if (fieldLower.includes('bubbling')) return '🫧';
    if (fieldLower.includes('drain')) return '💧';
    if (fieldLower.includes('dressing')) return '🩹';
    return null;
  };

  // ✅ Render compact field with HEALTHKON COLORS & RED ALERTS
  const renderCompactField = (label: string, value: string | undefined, showIcon: boolean = true, record:any = null) => {
    if (!value || value === 'undefined') return null;
    
    const status = getAbnormalityStatus(value);
    const icon = showIcon ? getCompactIcon(label) : null;
    
    return (
      <div className={`flex items-center justify-between py-2 px-3 rounded-lg text-sm ${
        status === 'critical' ? 'bg-red-50 border-l-4 border-red-600' : // ✅ RED ALERT
        status === 'warning' ? 'bg-[#FFF4E6] border-l-4 border-[#F07D13]' : // ✅ ORANGE WARNING
        'hover:bg-gray-50'
      }`}>
        <span className={`flex items-center gap-2 ${
          status === 'critical' ? 'text-red-700 font-semibold' : // ✅ RED TEXT for abnormal
          status === 'warning' ? 'text-[#F07D13] font-medium' : // ✅ ORANGE TEXT for warning
          'text-[#494949]' // ✅ HEALTHKON GREY for normal
        }`}>
          {icon && <span className="text-base">{icon}</span>}
          <span className="text-xs">{label}</span>
        </span>
        {getCompactBadge(value)} 
        <span className="text-xs text-gray-500">
          {formatTime(record.recorded_at || record.created_at)}
        </span>
      </div>
    );
  };

  // Render grouped compact section (supports multiple vitals in array)
  const renderCompactSection = (
    title: string,
    icon: React.ReactNode,
    data: TaskRecord[] | null,
    taskLabels: Record<string, string>,
    categoryKey: string,
    priority: 'high' | 'medium' | 'low' = 'medium'
  ) => {
    
    if (!Array.isArray(data) || data.length === 0) return null;

    // Extract all task entries from records
    const allEntries = data.flatMap(record =>
      record.task_data
        ? Object.entries(record.task_data).map(([k, v]) => ({ key: k, task: v, record }))
        : []
    );

    const hasData = allEntries.some(e => e.task?.result || e.task?.dropdown);
    if (!hasData) return null;

    // ---- GROUP similar tasks together ----
    const grouped = allEntries.reduce((acc, item) => {
      if (!acc[item.key]) acc[item.key] = [];
      acc[item.key].push(item);
      return acc;
    }, {} as Record<string, typeof allEntries>);

    const isExpanded = expandedSections.has(categoryKey);
    const totalCount = allEntries.length;

    // Abnormality level detection
    const hasCritical = false; //allEntries.some(e => getAbnormalityStatus(e.task.result) === "critical");
    const hasWarning  = false; //allEntries.some(e => getAbnormalityStatus(e.task.result) === "warning");

    return (
      <div className={`bg-white border-2 rounded-xl overflow-hidden shadow-sm ${
        hasCritical ? 'border-red-500' : 
        hasWarning ? 'border-[#F07D13]' : 
        'border-[#E2E8F0]'
      }`}>

        {/* Header */}
        <button
          onClick={() => toggleSection(categoryKey)}
          className={`w-full px-4 py-3 border-b-2 hover:opacity-80 transition-all ${
            hasCritical ? 'bg-red-50 border-red-500' : // ✅ RED BACKGROUND for critical
            hasWarning ? 'bg-[#FFF4E6] border-[#F07D13]' : // ✅ ORANGE BACKGROUND for warning
            'bg-[#E8F2FC] border-[#2762AC]/20' // ✅ HEALTHKON BLUE
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`${
                hasCritical ? 'text-red-700' : 
                hasWarning ? 'text-[#F07D13]' : 
                'text-[#2762AC]' // ✅ HEALTHKON PRIMARY BLUE
              }`}>{icon}</div>
              <h4 className={`text-sm font-semibold m-0 p-0 ${
                hasCritical ? 'text-red-700' : 
                hasWarning ? 'text-[#F07D13]' : 
                'text-[#2762AC]' // ✅ HEALTHKON PRIMARY BLUE
              }`}>{title}</h4>
              {hasCritical && (
                <Badge className="text-xs px-2 py-0.5 bg-red-600 text-white border-0 flex items-center gap-1 animate-pulse">
                  <AlertTriangle className="w-3 h-3" />
                  ALERT
                </Badge>
              )}
              {!hasCritical && hasWarning && (
                <Badge className="text-xs px-2 py-0.5 bg-[#F07D13] text-white border-0">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  Warning
                </Badge>
              )}
              <span className="text-xs text-[#494949]">({totalCount})</span>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 px-2 text-xs text-[#494949] hover:text-[#2762AC] hover:bg-white/50"
                  onClick={(e) => { e.stopPropagation(); /* Handle notify */ }}
                  type="button"
                >
                  <Bell className="w-3 h-3" />
                </Button>
                {isExpanded ? 
                  <ChevronUp className={`w-5 h-5 ${hasCritical ? 'text-red-700' : hasWarning ? 'text-[#F07D13]' : 'text-[#2762AC]'}`} /> : 
                  <ChevronDown className={`w-5 h-5 ${hasCritical ? 'text-red-700' : hasWarning ? 'text-[#F07D13]' : 'text-[#2762AC]'}`} />
                }
              </div>
            </div>
          </div>
        </button>

        {/* Collapsible Content */}
        {isExpanded && (
          <div className="p-3 bg-white grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">

            {Object.entries(grouped).map(([key, items]) => {
              const label = taskLabels[key] || key;
              const count = items.length;

              return (
                <div key={key} className="border rounded-lg p-3 bg-[#FAFAFA]">

                  {/* Task Header */}
                  <div className="flex justify-between items-center mb-2">
                    <p className="font-semibold text-[#2762AC]">{label}</p>
                    <span className="text-xs text-gray-700 bg-gray-200 px-2 py-0.5 rounded-full">
                      {count}
                    </span>
                  </div>

                  {/* Task Row Items */}
                  <div className="space-y-2 text-sm">
                    {items.map(({ task, record }, index) => {
                      const value = task.dropdown || task.result || "—";
                      const label = taskLabels[key] || key;

                      // 🔥 Time in AM/PM
                      const rawTime = task.timeTaken ?? record?.recorded_at?.slice(11, 16) ?? null;
                      const time = rawTime
                        ? new Date(`2000-01-01T${rawTime}`).toLocaleTimeString("en-IN", {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          })
                            .replace(" am", " AM")
                            .replace(" pm", " PM")
                        : "—";


                      // Date in readable IST format
                      const date = record?.recorded_at
                        ? new Date(record.recorded_at).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric"
                          })
                        : "—";

                      return (
                        <div key={key}>
                          {renderCompactField(label, value, true, record)}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

          </div>
        )}
      </div>
    );
  };


  // Patient Summary Header
  const renderPatientSummary = () => {
    const hasAnyAlert = [vitalsData, chestDrainData, nutritionData, tracheostomyData, 
                         woundDressingData, physiotherapyData, intakeOutputData]
      .some(data => {
        if (!data?.task_data) return false;
        return Object.values(data.task_data).some(task => {
          const value = task?.dropdown || task?.result || '';
          return getAbnormalityStatus(value) === 'critical';
        });
      });

    const handleToggleCompactMode = () => {
      const newMode = !compactMode;
      console.log('Toggle compact mode:', compactMode, '→', newMode);
      setCompactMode(newMode);
    };

    const handleToggleAllSections = () => {
      if (allSectionsExpanded) {
        collapseAllSections();
      } else {
        expandAllSections();
      }
    };

    return (
      <div className="bg-white border-2 border-[#2762AC] rounded-xl p-4 mb-4 sticky top-0 z-10 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <h3 className="text-lg text-[#2762AC] font-bold m-0 p-0 flex items-center gap-2">
                {patientName}
                {hasAnyAlert && (
                  <Badge className="bg-red-600 text-white border-0 text-xs animate-pulse">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    CRITICAL ALERTS
                  </Badge>
                )}
              </h3>
              <div className="flex items-center gap-3 mt-1 text-xs text-[#494949]">
                {patientAge && <span className="font-medium">{patientAge}y</span>}
                {patientGender && <span>• {patientGender}</span>}
                <span>• ID: {patientId}</span>
                {diagnosis && <span>• {diagnosis}</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* ✅ EXPAND ALL / COLLAPSE ALL Button */}
            <Button
              size="sm"
              variant="outline"
              onClick={handleToggleAllSections}
              className="h-8 text-xs border-[#2762AC] text-[#2762AC] hover:bg-[#E8F2FC]"
              type="button"
            >
              {allSectionsExpanded ? <ChevronsUp className="w-3 h-3 mr-1" /> : <ChevronsDown className="w-3 h-3 mr-1" />}
              {allSectionsExpanded ? 'Collapse All' : 'Expand All'}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="py-12">
        <LoadingState message="Loading nurse task data..." />
      </div>
    );
  }

  const hasAnyData = (vitalsData?.length > 0) || (chestDrainData?.length > 0) || (nutritionData?.length > 0) || (tracheostomyData?.length > 0) || (woundDressingData?.length > 0) || (physiotherapyData?.length > 0) || (intakeOutputData?.length > 0);
  
  // Task labels
  const vitalsLabels: Record<string, string> = {
    'Temp': 'Temp',
    'HR': 'HR',
    'RR': 'RR',
    'SPO2': 'SpO2',
    'BP': 'BP',
    'Breathing Pattern': 'Breathing',
    'Cough': 'Cough',
    'Sputum Color': 'Sputum',
    'Oxygen Requirement': 'O2 Req',
    'Device': 'Device',
    'Signs of Rejection/Infection': 'Infection',
  };

  const chestDrainLabels: Record<string, string> = {
    'Drain Side': 'Side',
    'Drain Volume': 'Volume',
    'Drain Color': 'Color',
    'Drain Dressing': 'Dressing',
    'Water Seal Column Movement': 'Water Seal',
    'Bubbling': 'Bubbling',
  };

  const nutritionLabels: Record<string, string> = {
    'Appetite': 'Appetite',
    'Intake Type': 'Intake',
    'Diet Remarks': 'Diet',
    'Stool Frequency': 'Stool',
  };

  const tracheostomyLabels: Record<string, string> = {
    'Stoma Site Condition': 'Stoma',
    'Tracheostomy Tube': 'Trach Tube',
    'Suctioning Done': 'Suction',
    'Frequency': 'Frequency',
    'Secretion Type': 'Secretion',
    'Subglottic Suction per 24 hours': 'Subglottic',
    'Subglottic Pressure': 'Pressure',
    'Oxygen Requirement': 'O2',
    'Humidification': 'Humidity',
  };

  const woundDressingLabels: Record<string, string> = {
    'Dressing': 'Dressing',
    'Date of Last Dressing Change': 'Last Change',
    'Patient Mobility': 'Mobility',
    'Wound Appearance': 'Appearance',
    'Wound Image': 'Image',
  };

  const physiotherapyLabels: Record<string, string> = {
    'Physiotherapy Given': 'Physio',
    'Ambulation': 'Ambulation',
    'Breathing Exercises': 'Exercises',
    'Walk Distance': 'Distance',
    'Oxygen Drop While Walking': 'O2 Drop',
    'FEV1': 'FEV1',
  };

  const intakeOutputLabels: Record<string, string> = {
    'Total Intake': 'Intake',
    'Total Output': 'Output',
    'Total Urine Output': 'Urine',
    'Total Drain': 'Drain',
  };

  const handleDateFilterChange = async (range: string) => {
    setSelectedDateFilter(range);
    const today = new Date();
    const format = (d: Date) => d.toISOString().split("T")[0];

    let start: string | undefined;
    let end: string | undefined = format(today);

    if(range=="overall"){
      range = "90";
    }
    
    switch (range) {
      case "today":
        start = end;
        break;

      case "yesterday":
        const y = new Date();               // new copy
        y.setDate(y.getDate() - 1);
        start = end = format(y);
        break;

      case "7":
      case "14":
      case "30":
      case "90":
        const back = new Date();            // new copy
        back.setDate(back.getDate() - Number(range));
        start = format(back);
        break;

      case "overall":
        start = end = undefined;
        break;
    }
    await fetchAllTaskData(start, end);
  };

  return (
    <div>
      {renderPatientSummary()}
      
      <ScrollArea className="h-[calc(100vh-250px)]">
        <div className="space-y-4 pr-4">          
          <div className="flex justify-end mb-3">
            <label className="text-sm font-medium text-gray-700 mt-3 mr-2">
              Show the Records of 
            </label>

            <select 
              className="border px-3 py-2 rounded-md text-sm cursor-pointer focus: outline-none"  
              onChange={(e) => handleDateFilterChange(e.target.value)}     
              value = {selectedDateFilter}    
            >
              <option selected value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="7">Last 7 Days</option>
              <option value="14">Last 14 Days</option>
              <option value="30">Last 30 Days</option>
              <option value="overall">Overall</option>
            </select>
          </div>

          {!hasAnyData && (
            <div className="text-center py-12 text-[#494949]">
              <AlertCircle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p>No home nurse task data available</p>
            </div>
          )}
          
          {renderCompactSection('Vitals', <Activity className="w-4 h-4" />, vitalsData, vitalsLabels, 'vitals', 'high')}
          {renderCompactSection('Chest Drain', <Droplet className="w-4 h-4" />, chestDrainData, chestDrainLabels, 'chest_drain', 'high')}
          {renderCompactSection('Nutrition', <Utensils className="w-4 h-4" />, nutritionData, nutritionLabels, 'nutrition', 'medium')}
          {renderCompactSection('Tracheostomy', <Wind className="w-4 h-4" />, tracheostomyData, tracheostomyLabels, 'tracheostomy', 'high')}
          {renderCompactSection('Wound Care', <Scissors className="w-4 h-4" />, woundDressingData, woundDressingLabels, 'wound_dressing', 'medium')}
          {renderCompactSection('Physiotherapy', <Dumbbell className="w-4 h-4" />, physiotherapyData, physiotherapyLabels, 'physiotherapy', 'medium')}
          {renderCompactSection('I/O', <TrendingUp className="w-4 h-4" />, intakeOutputData, intakeOutputLabels, 'intake_output', 'low')}
        </div>
      </ScrollArea>
    </div>
  );
}
